import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MotionConfig } from 'motion/react';
import { ClerkProvider } from '@clerk/react';
import { dark } from '@clerk/themes';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router';
import './app.css';
import { App } from './app.js';
import { readConsentFromCookie } from './lib/cookie-consent.js';
import { i18nInstance } from './lib/i18n.js';
import { queryClient } from './lib/query-client.js';
import { initializeWebSentry, getWebSentryConfig, SentryAppBoundary } from './lib/sentry.js';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY');
}

initializeWebSentry(
  getWebSentryConfig({
    origin: globalThis.location.origin,
    apiBase: import.meta.env.VITE_API_BASE,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    enableOptionalTelemetry: readConsentFromCookie().analytics,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
  }),
);

const clerkAppearance = {
  baseTheme: dark,
  variables: {
    colorPrimary: '#256eff',
    colorBackground: '#141414',
    colorText: '#eeeeff',
    colorTextSecondary: '#8888aa',
    colorInputBackground: '#1e1e1e',
    borderRadius: '8px',
  },
  elements: {
    card: 'bg-[#141414] border border-[rgba(255,255,255,0.04)] shadow-none rounded-lg',
    formFieldInput: 'bg-[#1e1e1e] border-[rgba(255,255,255,0.04)] text-[#eeeeff] rounded',
    formButtonPrimary: 'bg-[#256eff] hover:bg-[#1a5cd4] rounded',
    footerActionLink: 'text-[#256eff]',
    userButtonAvatarBox: 'w-8 h-8',
  },
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MotionConfig reducedMotion="user">
      <ClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY}
        appearance={clerkAppearance}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        afterSignOutUrl="/"
        signUpFallbackRedirectUrl="/"
        signInFallbackRedirectUrl="/dashboard"
      >
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <I18nextProvider i18n={i18nInstance}>
              <SentryAppBoundary>
                <App />
              </SentryAppBoundary>
            </I18nextProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ClerkProvider>
    </MotionConfig>
  </StrictMode>,
);
